import { useState, useCallback, useRef, useEffect } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import {
  EmergencyLightingOnEveryFloorStatus,
  FireExtinguisherOnEveryFloorStatus,
  FireExtinguisherInElevatorRoomStatus,
  SmokeDetectorsOnEveryFloorStatus,
  FireHoseReelStatus,
  FireProofDoorsStatus,
  FireEvacuationStatus,
  FireStairStatus
} from "shared/types/fire-security";

export interface FireSafetyInfo {
  id: number;
  type: 'building' | 'unit';
  location: string;
  formattedLocation: string;
  emergencyLightingOnEveryFloorStatus: string;
  fireExtinguisherOnEveryFloorStatus: string;
  fireExtinguisherInElevatorRoomStatus: string;
  smokeDetectorsOnEveryFloorStatus: string;
  fireHoseReelStatus: string;
  fireProofDoors: string;
  fireEvacuationStatus: string;
  fireStairStatus: string;
  fireSecurityLastInspectionDate: string | null;
  fireSecurityNextInspectionDate: string | null;
  remarks: string | null;
  hasRemarks: boolean;
}

export interface ActionableIssuesSummary {
  rookmelders: number;
  brandblussers: number;
  brandhaspel: number;
  noodverlichting: number;
  branddeuren: number;
  evacuatie: number;
  brandtrap: number;
  keuring: number;
  keuringVervallen: number;
  inspectiedatumNietIngepland: number;
  rookmeldersOnbekend: number;
  brandblusersOnbekend: number;
  brandhaspelOnbekend: number;
  noodverlichtingOnbekend: number;
  brandeurenOnbekend: number;
  evacuatieOnbekend: number;
  brandtrapOnbekend: number;
}

// Status values constants
export const StatusValues = {
  OK: 'Aanwezig',
  PARTIAL: 'Gedeeltelijk aanwezig',
  NOT_OK: 'Niet aanwezig',
  NOT_REQUIRED_EMERGENCY: 'Niet nodig',
  NOT_REQUIRED_GENERAL: 'Niet verplicht',
  NOT_REQUIRED_HOSE: 'Geen brandhaspel verplicht',
  NOT_REQUIRED_EVACUATION: 'Geen pictogrammen verplicht',
  NOT_REQUIRED_STAIR: 'Geen brandtrap verplicht',
  UNKNOWN: 'Niet nagekeken',
  NO_ELEVATOR: 'Geen lift aanwezig',
  FIRE_STAIR_NOT_OK: 'Verplicht, en niet aanwezig'
};

// Format location ID to remove "Building" or "Unit" prefix
const formatLocationId = (location: string) => {
  return location.replace("Building ", "").replace("Unit ", "");
};

// Calculate actionable issues from data
const calculateActionableIssues = (data: FireSafetyInfo[]): ActionableIssuesSummary => {
  const summary: ActionableIssuesSummary = {
    rookmelders: 0,
    brandblussers: 0,
    brandhaspel: 0,
    noodverlichting: 0,
    branddeuren: 0,
    evacuatie: 0,
    brandtrap: 0,
    keuring: 0,
    keuringVervallen: 0,
    inspectiedatumNietIngepland: 0,
    rookmeldersOnbekend: 0,
    brandblusersOnbekend: 0,
    brandhaspelOnbekend: 0,
    noodverlichtingOnbekend: 0,
    brandeurenOnbekend: 0,
    evacuatieOnbekend: 0,
    brandtrapOnbekend: 0
  };

  data.forEach(item => {
    // Rookmelders niet in orde
    if (item.smokeDetectorsOnEveryFloorStatus === SmokeDetectorsOnEveryFloorStatus.NOT_OK) {
      summary.rookmelders++;
    }
    if (item.smokeDetectorsOnEveryFloorStatus === SmokeDetectorsOnEveryFloorStatus.UNKNOWN) {
      summary.rookmeldersOnbekend++;
    }

    // Brandblussers
    if (item.fireExtinguisherOnEveryFloorStatus === FireExtinguisherOnEveryFloorStatus.NOT_OK) {
      summary.brandblussers++;
    }
    if (item.fireExtinguisherOnEveryFloorStatus === FireExtinguisherOnEveryFloorStatus.UNKNOWN) {
      summary.brandblusersOnbekend++;
    }

    // Brandhaspel
    if (item.fireHoseReelStatus === FireHoseReelStatus.NOT_OK) {
      summary.brandhaspel++;
    }
    if (item.fireHoseReelStatus === FireHoseReelStatus.UNKNOWN) {
      summary.brandhaspelOnbekend++;
    }

    // Noodverlichting
    if (item.emergencyLightingOnEveryFloorStatus === EmergencyLightingOnEveryFloorStatus.NOT_OK) {
      summary.noodverlichting++;
    }
    if (item.emergencyLightingOnEveryFloorStatus === EmergencyLightingOnEveryFloorStatus.UNKNOWN) {
      summary.noodverlichtingOnbekend++;
    }

    // Branddeuren
    if (item.fireProofDoors === FireProofDoorsStatus.NOT_OK) {
      summary.branddeuren++;
    }
    if (item.fireProofDoors === FireProofDoorsStatus.UNKNOWN) {
      summary.brandeurenOnbekend++;
    }

    // Evacuatie
    if (item.fireEvacuationStatus === FireEvacuationStatus.NOT_OK) {
      summary.evacuatie++;
    }
    if (item.fireEvacuationStatus === FireEvacuationStatus.UNKNOWN) {
      summary.evacuatieOnbekend++;
    }

    // Brandtrap
    if (item.fireStairStatus === FireStairStatus.NOT_OK) {
      summary.brandtrap++;
    }
    if (item.fireStairStatus === FireStairStatus.UNKNOWN) {
      summary.brandtrapOnbekend++;
    }

    // Inspection dates
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    if (item.fireSecurityNextInspectionDate) {
      const nextInspectionDate = new Date(item.fireSecurityNextInspectionDate);
      if (nextInspectionDate < today) {
        summary.keuringVervallen++;
      } else if (nextInspectionDate <= thirtyDaysFromNow) {
        summary.keuring++;
      }
    } else {
      summary.inspectiedatumNietIngepland++;
    }
  });

  return summary;
};

export function useFireSafetyData() {
  const { session } = useSession();
  const [fireSafetyInfo, setFireSafetyInfo] = useState<FireSafetyInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use a ref to track if data has been initially loaded
  // This prevents re-fetching when switching browser tabs
  const hasInitiallyLoaded = useRef(false);
  const isCurrentlyFetching = useRef(false);

  const fetchFireSafetyInfo = useCallback(async (force = false) => {
    // Skip if already loaded and not forcing refresh
    if (!force && hasInitiallyLoaded.current && fireSafetyInfo.length > 0) {
      return;
    }

    // Prevent concurrent fetches
    if (isCurrentlyFetching.current) {
      return;
    }

    if (!session) {
      return;
    }

    isCurrentlyFetching.current = true;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/maintenance/fire-safety-info`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      // Process data
      const processedData: FireSafetyInfo[] = data.map((item: any) => ({
        ...item,
        formattedLocation: formatLocationId(item.location),
        hasRemarks: item.remarks && item.remarks.trim().length > 0
      }));

      setFireSafetyInfo(processedData);
      hasInitiallyLoaded.current = true;
    } catch (err) {
      console.error("Error fetching fire safety info:", err);
      setError("Kon brandveiligheidsinformatie niet laden");
      toast.error("Kon brandveiligheidsinformatie niet laden");
    } finally {
      setIsLoading(false);
      isCurrentlyFetching.current = false;
    }
  }, [session, fireSafetyInfo.length]);

  // Debounced update function
  const updateFieldDebounced = useCallback(
    debounce(async (id: number, type: string, field: string, value: string | null) => {
      if (!session) return;

      try {
        const response = await fetchWithAuth(
          `${API_URL}/fire-security/update-field`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, type, field, value }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        toast.success("Bijgewerkt", { autoClose: 1000 });
      } catch (err) {
        console.error("Error updating field:", err);
        toast.error("Kon veld niet bijwerken");
        // Refresh to get the original data on error
        fetchFireSafetyInfo(true);
      }
    }, 500),
    [session, fetchFireSafetyInfo]
  );

  // Update field with optimistic update
  const updateField = useCallback((id: number, type: string, field: string, value: string | null) => {
    let processedValue = value;

    // Handle date fields
    if (field.includes('Date') && value && value !== "-") {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          processedValue = date.toISOString().split('T')[0];
        } else {
          toast.error("Ongeldige datum");
          return;
        }
      } catch {
        toast.error("Ongeldige datum");
        return;
      }
    } else if (field.includes('Date') && (!value || value === "-")) {
      processedValue = null;
    }

    // Optimistic update
    setFireSafetyInfo(prevInfo =>
      prevInfo.map(item =>
        (item.id === id && item.type === type)
          ? { ...item, [field]: processedValue }
          : item
      )
    );

    // Send to backend
    updateFieldDebounced(id, type, field, processedValue);
  }, [updateFieldDebounced]);

  // Copy field to multiple targets
  const copyFieldToTargets = useCallback(async (
    sourceInfo: FireSafetyInfo,
    field: string,
    targetItems: { id: number; type: string }[]
  ) => {
    if (!session || targetItems.length === 0) {
      toast.error("Selecteer ten minste één doel om te kopiëren");
      return false;
    }

    try {
      const copyPromises = targetItems.map(async (target) => {
        return fetchWithAuth(
          `${API_URL}/fire-security/update-field`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: target.id,
              type: target.type,
              field,
              value: sourceInfo[field as keyof FireSafetyInfo]
            }),
          },
          session
        );
      });

      const results = await Promise.all(copyPromises);
      const allSuccessful = results.every(res => res.ok);

      if (allSuccessful) {
        toast.success(`Succesvol gekopieerd naar ${targetItems.length} item(s)`);
        fetchFireSafetyInfo(true);
        return true;
      } else {
        toast.error("Er is een fout opgetreden bij het kopiëren naar sommige items");
        return false;
      }
    } catch (err) {
      console.error("Error copying field:", err);
      toast.error("Kon veld niet kopiëren");
      return false;
    }
  }, [session, fetchFireSafetyInfo]);

  // Refresh data (force fetch)
  const refresh = useCallback(() => {
    fetchFireSafetyInfo(true);
  }, [fetchFireSafetyInfo]);

  // Initial fetch on mount - only if not already loaded
  useEffect(() => {
    if (session && !hasInitiallyLoaded.current) {
      fetchFireSafetyInfo();
    }
  }, [session, fetchFireSafetyInfo]);

  // Calculate actionable issues
  const actionableIssues = calculateActionableIssues(fireSafetyInfo);

  // Get status options for a field
  const getStatusOptions = useCallback((fieldName: string): string[] => {
    switch(fieldName) {
      case 'emergencyLightingOnEveryFloorStatus':
        return Object.values(EmergencyLightingOnEveryFloorStatus);
      case 'smokeDetectorsOnEveryFloorStatus':
        return Object.values(SmokeDetectorsOnEveryFloorStatus);
      case 'fireExtinguisherOnEveryFloorStatus':
        return Object.values(FireExtinguisherOnEveryFloorStatus);
      case 'fireExtinguisherInElevatorRoomStatus':
        return Object.values(FireExtinguisherInElevatorRoomStatus);
      case 'fireHoseReelStatus':
        return Object.values(FireHoseReelStatus);
      case 'fireProofDoors':
        return Object.values(FireProofDoorsStatus);
      case 'fireEvacuationStatus':
        return Object.values(FireEvacuationStatus);
      case 'fireStairStatus':
        return Object.values(FireStairStatus);
      default:
        return Object.values(StatusValues);
    }
  }, []);

  return {
    fireSafetyInfo,
    isLoading,
    error,
    actionableIssues,
    totalItems: fireSafetyInfo.length,
    updateField,
    copyFieldToTargets,
    refresh,
    getStatusOptions,
    hasLoaded: hasInitiallyLoaded.current
  };
}
